/**
 * Phase 24.4a — Toast primitive + ToastContainer.
 *
 * ToastContainer mounts inside <BrowserRouter>, sibling to <Routes>.
 * Position: fixed top-4 right-4 z-[60]. Single-toast dispatcher (replace
 * semantics in store). Auto-dismiss timer + manual dismiss X button.
 *
 * Variants:
 *   success — amber accent (positive action confirmation)
 *   error   — coral accent (mutation failed, retry possible)
 *   info    — navy-soft accent (neutral informational)
 */
import { useEffect } from "react";
import { X, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast, type ToastVariant } from "@/stores/toast";

const VARIANT_STYLES: Record<ToastVariant, { border: string; icon: typeof CheckCircle2; iconColor: string }> = {
  success: { border: "border-amber", icon: CheckCircle2, iconColor: "text-amber-dark" },
  error: { border: "border-coral", icon: AlertCircle, iconColor: "text-coral" },
  info: { border: "border-navy-soft", icon: Info, iconColor: "text-navy-soft" },
};

export function ToastContainer() {
  const { currentToast, dismiss } = useToast();

  useEffect(() => {
    if (!currentToast) return;
    if (currentToast.durationMs == null) return;
    const timer = window.setTimeout(() => dismiss(), currentToast.durationMs);
    return () => window.clearTimeout(timer);
  }, [currentToast, dismiss]);

  if (!currentToast) return null;

  const style = VARIANT_STYLES[currentToast.variant];
  const IconComp = style.icon;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed top-4 right-4 z-[60] flex items-start gap-3 rounded-lg border-l-4 bg-cream-light p-4 pr-3 shadow-soft min-w-[260px] max-w-md",
        style.border,
      )}
      data-testid="toast-container"
      data-variant={currentToast.variant}
    >
      <IconComp
        className={cn("h-5 w-5 shrink-0 mt-0.5", style.iconColor)}
        strokeWidth={2}
        aria-hidden="true"
      />
      <p className="flex-1 text-[13px] text-navy-deep" data-testid="toast-message">
        {currentToast.message}
      </p>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded p-1 text-taupe hover:bg-cream-border hover:text-navy-deep"
        data-testid="toast-dismiss"
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}
